import { Typography } from "@king-ict/eupisi-ui/components";
import type { CodeBook } from "@king-ict/eupisi-ui/models";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import StaticField from "../../../../components/StaticField";
import type { ProgramCodeBook } from "../../../../models/Program";
import { OtherEnrollmentConditionId, OtherFormOfTeachingId, OtherKnowledgeCheckId } from "../../../../models/ProgramRequest";
import { getSelectedCodeBookNames, getTotalProgramDuration } from "../utils";
import { renderModules, renderStringFieldsOrPlaceholder } from "./utils";

type IProps = {
  programName: ProgramCodeBook;
  formsOfTeaching?: CodeBook[];
  knowledgeChecks?: CodeBook[];
  enrollmentConditions?: CodeBook[];
};

export const ProgramNameMinisterFieldsDetail = ({
  programName,
  formsOfTeaching,
  knowledgeChecks,
  enrollmentConditions,
}: IProps) => {
  const { t } = useTranslation();

  const detail = programName.ministerDetail;
  const competences = detail?.competences ?? [];
  const modules = detail?.modules ?? [];
  const totalDuration = getTotalProgramDuration(programName);

  return (
    <>
      <Row>
        <Col md={3} className="mt-5">
          <StaticField label={t("common.programDuration")} value={totalDuration} />
        </Col>
        <Col md={3} className="mt-5">
          <StaticField label={t("request.guidedLearningDuration")} value={detail?.guidedLearningDuration ?? 0} />
        </Col>
        <Col md={3} className="mt-5">
          <StaticField label={t("request.workLearningDuration")} value={detail?.workLearningDuration ?? 0} />
        </Col>
        <Col md={3} className="mt-5">
          <StaticField
            label={t("request.independentLearningDuration")}
            value={detail?.independentLearningDuration ?? 0}
          />
        </Col>
      </Row>

      <Row>
        <Col md={4} className="mt-5">
          <StaticField
            label={t("request.formOfTeaching")}
            value={getSelectedCodeBookNames(detail?.formOfTeachingIds, formsOfTeaching)}
          />
        </Col>
        <Col md={4} className="mt-5">
          <StaticField
            label={t("request.knowledgeChecks")}
            value={getSelectedCodeBookNames(detail?.knowledgeCheckIds, knowledgeChecks)}
          />
        </Col>
        <Col md={4} className="mt-5">
          <StaticField
            label={t("request.enrollmentConditions")}
            value={getSelectedCodeBookNames(detail?.enrollmentConditionIds, enrollmentConditions)}
          />
        </Col>
      </Row>

      {(detail?.formOfTeachingIds ?? []).includes(OtherFormOfTeachingId) && (
        <Row>
          <Col md={12} className="mt-5">
            <StaticField
              label={t("request.otherFormsOfTeaching")}
              value={(detail?.otherFormsOfTeaching ?? []).join(", ") || "--"}
            />
          </Col>
        </Row>
      )}

      {(detail?.knowledgeCheckIds ?? []).includes(OtherKnowledgeCheckId) && (
        <Row>
          <Col md={12} className="mt-5">
            <StaticField
              label={t("request.otherKnowledgeChecks")}
              value={(detail?.otherKnowledgeChecks ?? []).join(", ") || "--"}
            />
          </Col>
        </Row>
      )}

      {(detail?.enrollmentConditionIds ?? []).includes(OtherEnrollmentConditionId) && (
        <Row>
          <Col md={12} className="mt-5">
            <StaticField
              label={t("request.otherEnrollmentConditions")}
              value={(detail?.otherEnrollmentConditions ?? []).join(", ") || "--"}
            />
          </Col>
        </Row>
      )}

      <Row className="mt-4">
        <Col md={12}>
          <Typography variant="smallHeading" component="h3">
            {t("request.competences")}
          </Typography>
        </Col>
      </Row>
      <Row>
        <Col md={12} className="mt-3">
          {renderStringFieldsOrPlaceholder(t("request.competenceName"), competences)}
        </Col>
      </Row>

      <Row className="mt-4">
        <Col md={12}>
          <Typography variant="smallHeading" component="h3">
            {t("request.modules")}
          </Typography>
        </Col>
      </Row>
      {renderModules(t, modules)}
    </>
  );
};

